import React, { useCallback, useEffect, useState } from 'react';
import { Upload, Plus, X, CheckCircle, AlertCircle, Loader } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

interface KBDocument {
  id: string;
  name: string;
  description: string;
  source_type: 'file' | 'text' | 'url';
  source_url?: string;
  sync_status: 'pending' | 'synced' | 'failed' | 'deleted';
  rag_indexed: boolean;
  linked_to_agent?: boolean;
  linked_double_ids?: string[];
  linked_double_count?: number;
  file_size?: number;
  file_type?: string;
  created_at: string;
  sync_error?: string;
}

interface KBSyncStatus {
  total_documents: number;
  synced_documents: number;
  pending_documents: number;
  failed_documents: number;
  rag_indexed_documents: number;
}

interface KnowledgeBaseManagerProps {
  doubleId: string;
  onClose?: () => void;
}

const KnowledgeBaseManager: React.FC<KnowledgeBaseManagerProps> = ({ doubleId, onClose }) => {
  const [activeTab, setActiveTab] = useState<'documents' | 'library' | 'upload' | 'text' | 'url'>('documents');
  const [documents, setDocuments] = useState<KBDocument[]>([]);
  const [libraryDocuments, setLibraryDocuments] = useState<KBDocument[]>([]);
  const [syncStatus, setSyncStatus] = useState<KBSyncStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLibraryLoading, setIsLibraryLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [libraryError, setLibraryError] = useState<string | null>(null);

  // File upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});

  // Text input state
  const [textContent, setTextContent] = useState('');
  const [textName, setTextName] = useState('');
  const [textDescription, setTextDescription] = useState('');
  const [isAddingText, setIsAddingText] = useState(false);

  // URL input state
  const [urlInput, setUrlInput] = useState('');
  const [urlName, setUrlName] = useState('');
  const [urlDescription, setUrlDescription] = useState('');
  const [isAddingUrl, setIsAddingUrl] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${API_BASE_URL}/api/knowledge-base/${doubleId}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch KB data: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setDocuments(data.documents || []);
        setSyncStatus(data.sync_summary);
      } else {
        setError(data.error || 'Failed to load knowledge base');
      }
    } catch (err) {
      console.error('Error fetching KB data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load knowledge base');
    } finally {
      setIsLoading(false);
    }
  }, [doubleId]);

  const fetchLibrary = useCallback(async () => {
    try {
      setIsLibraryLoading(true);
      setLibraryError(null);
      const response = await fetch(
        `${API_BASE_URL}/api/knowledge-base/${doubleId}/library`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch library: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        setLibraryDocuments(data.documents || []);
      } else {
        setLibraryError(data.error || 'Failed to load library');
      }
    } catch (err) {
      console.error('Error fetching library:', err);
      setLibraryError(err instanceof Error ? err.message : 'Failed to load library');
    } finally {
      setIsLibraryLoading(false);
    }
  }, [doubleId]);

  // Fetch documents and sync status
  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      fetchDocuments();
      fetchLibrary();
    }

    const interval = setInterval(fetchDocuments, 5000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [fetchDocuments, fetchLibrary]);

  // Handle file upload
  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile) return;

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      if (uploadName) formData.append('name', uploadName);
      if (uploadDescription) formData.append('description', uploadDescription);

      const response = await fetch(
        `${API_BASE_URL}/api/knowledge-base/${doubleId}/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setDocuments(prev => [...prev, data.document]);
        setUploadFile(null);
        setUploadName('');
        setUploadDescription('');
        setActiveTab('documents');
      } else {
        throw new Error(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle text addition
  const handleAddText = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!textContent.trim()) return;

    setIsAddingText(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/knowledge-base/${doubleId}/text`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            text: textContent,
            name: textName || 'Text Document',
            description: textDescription,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to add text: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setDocuments(prev => [...prev, data.document]);
        setTextContent('');
        setTextName('');
        setTextDescription('');
        setActiveTab('documents');
      } else {
        throw new Error(data.error || 'Failed to add text');
      }
    } catch (err) {
      console.error('Error adding text:', err);
      setError(err instanceof Error ? err.message : 'Failed to add text');
    } finally {
      setIsAddingText(false);
    }
  };

  // Handle URL addition
  const handleAddUrl = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlInput.trim()) return;

    setIsAddingUrl(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/knowledge-base/${doubleId}/url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            url: urlInput,
            name: urlName || urlInput,
            description: urlDescription,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to add URL: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setDocuments(prev => [...prev, data.document]);
        setUrlInput('');
        setUrlName('');
        setUrlDescription('');
        setActiveTab('documents');
      } else {
        throw new Error(data.error || 'Failed to add URL');
      }
    } catch (err) {
      console.error('Error adding URL:', err);
      setError(err instanceof Error ? err.message : 'Failed to add URL');
    } finally {
      setIsAddingUrl(false);
    }
  };

  // Handle document deletion
  const handleDeleteDocument = async (docId: string) => {
    if (!window.confirm('Remove this document from the selected double?')) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/knowledge-base/${doubleId}/${docId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setDocuments(prev => prev.filter(doc => doc.id !== docId));
        fetchLibrary();
      } else {
        throw new Error(data.error || 'Delete failed');
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    }
  };

  // Handle manual sync
  const handleManualSync = async () => {
    setIsSyncing(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/knowledge-base/${doubleId}/sync`,
        {
          method: 'POST',
        }
      );

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        await fetchDocuments();
      } else {
        throw new Error(data.error || 'Sync failed');
      }
    } catch (err) {
      console.error('Error syncing KB:', err);
      setError(err instanceof Error ? err.message : 'Failed to sync knowledge base');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAttachDocument = async (docId: string) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/knowledge-base/${doubleId}/attach`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ document_id: docId }),
        }
      );

      if (!response.ok) {
        throw new Error(`Attach failed: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setLibraryDocuments(prev => prev.filter(doc => doc.id !== docId));
        setDocuments(prev => [data.document, ...prev]);
        fetchDocuments();
        setActiveTab('documents');
      } else {
        throw new Error(data.error || 'Attach failed');
      }
    } catch (err) {
      console.error('Error attaching document:', err);
      setLibraryError(err instanceof Error ? err.message : 'Failed to attach document');
    }
  };

  const getSyncStatusColor = (status: string) => {
    switch (status) {
      case 'synced':
        return 'text-green-600';
      case 'pending':
        return 'text-yellow-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getSyncStatusIcon = (status: string) => {
    switch (status) {
      case 'synced':
        return <CheckCircle className="h-4 w-4" />;
      case 'pending':
        return <Loader className="h-4 w-4 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="w-full bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Knowledge Base</h3>
          <p className="text-sm text-gray-600 mt-1">Upload files, text, or URLs to train your double</p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Sync Status Summary */}
      {syncStatus && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="grid grid-cols-5 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-gray-900">{syncStatus.total_documents}</div>
              <div className="text-xs text-gray-600 mt-1">Total Docs</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{syncStatus.synced_documents}</div>
              <div className="text-xs text-gray-600 mt-1">Synced</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-yellow-600">{syncStatus.pending_documents}</div>
              <div className="text-xs text-gray-600 mt-1">Pending</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">{syncStatus.failed_documents}</div>
              <div className="text-xs text-gray-600 mt-1">Failed</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-blue-600">{syncStatus.rag_indexed_documents}</div>
              <div className="text-xs text-gray-600 mt-1">Indexed</div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('documents')}
          className={`px-4 py-3 font-medium border-b-2 transition-colors ${
            activeTab === 'documents'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Documents ({documents.length})
        </button>
        <button
          onClick={() => setActiveTab('library')}
          className={`px-4 py-3 font-medium border-b-2 transition-colors ${
            activeTab === 'library'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Library
        </button>
        <button
          onClick={() => setActiveTab('upload')}
          className={`px-4 py-3 font-medium border-b-2 transition-colors ${
            activeTab === 'upload'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Upload File
        </button>
        <button
          onClick={() => setActiveTab('text')}
          className={`px-4 py-3 font-medium border-b-2 transition-colors ${
            activeTab === 'text'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Add Text
        </button>
        <button
          onClick={() => setActiveTab('url')}
          className={`px-4 py-3 font-medium border-b-2 transition-colors ${
            activeTab === 'url'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
        >
          Add URL
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {libraryError && activeTab === 'library' && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {libraryError}
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 text-sm">No documents yet. Add one to get started!</p>
            </div>
          ) : (
            <>
              {documents.map(doc => (
                <div key={doc.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900">{doc.name}</h4>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {doc.source_type}
                        </span>
                      </div>
                      {doc.description && (
                        <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-600">
                        <div className="flex items-center gap-1">
                          {getSyncStatusIcon(doc.sync_status)}
                          <span className={getSyncStatusColor(doc.sync_status)}>
                            {doc.sync_status === 'synced' ? 'Synced' :
                             doc.sync_status === 'pending' ? 'Syncing...' : 'Failed'}
                          </span>
                        </div>
                        {doc.rag_indexed && (
                          <div className="flex items-center gap-1 text-blue-600">
                            <CheckCircle className="h-4 w-4" />
                            Indexed
                          </div>
                        )}
                        {doc.linked_to_agent && (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-4 w-4" />
                            Linked to Agent
                          </div>
                        )}
                        {doc.linked_double_count && doc.linked_double_count > 1 && (
                          <div className="flex items-center gap-1 text-purple-600">
                            <CheckCircle className="h-4 w-4" />
                            Used by {doc.linked_double_count} doubles
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteDocument(doc.id)}
                      className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={handleManualSync}
                disabled={isSyncing || documents.length === 0}
                className="w-full mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSyncing ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    Manual Sync
                  </>
                )}
              </button>
            </>
          )}
        </div>
      )}

      {activeTab === 'library' && (
        <div className="space-y-4">
          {isLibraryLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : libraryDocuments.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600 text-sm">No shared documents available.</p>
            </div>
          ) : (
            libraryDocuments.map(doc => (
              <div key={doc.id} className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-gray-900">{doc.name}</h4>
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                        {doc.source_type}
                      </span>
                    </div>
                    {doc.description && (
                      <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                    )}
                    {doc.linked_double_count && doc.linked_double_count > 0 && (
                      <div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        Used by {doc.linked_double_count} doubles
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => handleAttachDocument(doc.id)}
                    className="px-3 py-2 text-sm font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/5"
                  >
                    Attach
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Upload File Tab */}
      {activeTab === 'upload' && (
        <form onSubmit={handleFileUpload} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer"
              onClick={() => document.getElementById('fileInput')?.click()}
            >
              <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">
                {uploadFile ? uploadFile.name : 'Click to upload or drag and drop'}
              </p>
              <p className="text-xs text-gray-500 mt-1">PDF, DOCX, TXT, MD up to 50MB</p>
              <input
                id="fileInput"
                type="file"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className="hidden"
                accept=".pdf,.docx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
              />
            </div>
          </div>

          <div>
            <label htmlFor="uploadName" className="block text-sm font-medium text-gray-700 mb-2">
              Document Name (optional)
            </label>
            <input
              id="uploadName"
              type="text"
              value={uploadName}
              onChange={(e) => setUploadName(e.target.value)}
              placeholder="My Document"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

          <div>
            <label htmlFor="uploadDescription" className="block text-sm font-medium text-gray-700 mb-2">
              Description (optional)
            </label>
            <textarea
              id="uploadDescription"
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              placeholder="What is this document about?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-24"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setActiveTab('documents')}
              className="flex-1 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading || !uploadFile}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload File
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Add Text Tab */}
      {activeTab === 'text' && (
        <form onSubmit={handleAddText} className="space-y-4">
          <div>
            <label htmlFor="textContent" className="block text-sm font-medium text-gray-700 mb-2">
              Text Content *
            </label>
            <textarea
              id="textContent"
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Enter the text content for your knowledge base..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-40"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {textContent.length} / 100,000 characters
            </p>
          </div>

          <div>
            <label htmlFor="textName" className="block text-sm font-medium text-gray-700 mb-2">
              Document Name (optional)
            </label>
            <input
              id="textName"
              type="text"
              value={textName}
              onChange={(e) => setTextName(e.target.value)}
              placeholder="Text Document"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

          <div>
            <label htmlFor="textDescription" className="block text-sm font-medium text-gray-700 mb-2">
              Description (optional)
            </label>
            <textarea
              id="textDescription"
              value={textDescription}
              onChange={(e) => setTextDescription(e.target.value)}
              placeholder="What is this content about?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-20"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setActiveTab('documents')}
              className="flex-1 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isAddingText || !textContent.trim()}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isAddingText ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Add Text
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Add URL Tab */}
      {activeTab === 'url' && (
        <form onSubmit={handleAddUrl} className="space-y-4">
          <div>
            <label htmlFor="urlInput" className="block text-sm font-medium text-gray-700 mb-2">
              URL *
            </label>
            <input
              id="urlInput"
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              placeholder="https://example.com/article"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              required
            />
          </div>

          <div>
            <label htmlFor="urlName" className="block text-sm font-medium text-gray-700 mb-2">
              Link Name (optional)
            </label>
            <input
              id="urlName"
              type="text"
              value={urlName}
              onChange={(e) => setUrlName(e.target.value)}
              placeholder="My Article"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>

          <div>
            <label htmlFor="urlDescription" className="block text-sm font-medium text-gray-700 mb-2">
              Description (optional)
            </label>
            <textarea
              id="urlDescription"
              value={urlDescription}
              onChange={(e) => setUrlDescription(e.target.value)}
              placeholder="What is this article about?"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary h-20"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => setActiveTab('documents')}
              className="flex-1 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isAddingUrl || !urlInput.trim()}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isAddingUrl ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Add URL
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default KnowledgeBaseManager;
